import {History, Location, To} from 'history';
import {useEffect, useState} from 'react';
import {useHistory} from 'react-router-dom';

import {IHistoryLocationState} from './use-app-history-types';

type TAppLocation = Omit<Location, 'state'> & {
    state: IHistoryLocationState;
};

type THistoryListener = (v: TAppLocation) => void;

// не весь тип приведён в соответствие, дописать при необходимости
type TAppHistory = Omit<History, 'location' | 'listen'> & {
    location: TAppLocation;
    listen: (listener: THistoryListener) => Function;
};

export const useAppHistory = () => {
    const history: TAppHistory = useHistory();

    const getLocationState = () => history.location.state;

    const [currentState, setCurrentState] = useState<IHistoryLocationState>(getLocationState());

    const getPathname = (withoutSearchParams = false) => {
        if (withoutSearchParams) return history.location.pathname;
        const searchParams = new URLSearchParams(history.location.search);
        const pathname = Array.from(searchParams).length
            ? `${history.location.pathname}?${searchParams.toString()}`
            : history.location.pathname;
        return pathname;
    };

    useEffect(() => {
        const unlisten = history.listen((v: TAppLocation) => {
            setCurrentState(v?.state);
        });
        return () => {
            unlisten();
        };
    }, []);

    const getCurrentStateValue = <T extends keyof IHistoryLocationState>(key: T) => currentState?.[key];
    const setLocationState = (state: IHistoryLocationState, resetSearchParams = false) => {
        history.replace(getPathname(resetSearchParams), state);
    };
    const getLocationStateValue = <T extends keyof IHistoryLocationState>(key: T) => getLocationState()?.[key];
    const setLocationStateValue = <T extends keyof IHistoryLocationState>(
        key: T, value: IHistoryLocationState[T], resetSearchParams = false) => {
        history.replace(getPathname(resetSearchParams), {
            ...getLocationState(),
            [key]: value,
        });
    };
    const updateLocationState = (state: Partial<IHistoryLocationState>, resetSearchParams = false) => {
        history.replace(getPathname(resetSearchParams), {
            ...getLocationState(),
            ...state,
        });
    };
    const pushWithStateUpdate = (to: To, state: Partial<IHistoryLocationState>) => {
        history.push(to, {
            ...getLocationState(),
            ...state,
        });
    };

    return {
        ...history,
        currentState,
        getCurrentStateValue,
        getLocationState,
        setLocationState, // (!) заменяет всё состояние
        getLocationStateValue,
        setLocationStateValue,
        updateLocationState,
        pushWithStateUpdate,
    };
};

export type TUseAppHistoryFunction = ReturnType<typeof useAppHistory>;
